// MA19 합배송스케쥴 엑셀 업로드 파싱 (TM-4166).
//  1행 헤더로 컬럼을 찾는다. 2.0 다운로드 양식의 한글 헤더(결제일…)와 1.0 MA10 다운로드 헤더(컬럼코드 CLS_DATE…)를 모두 받는다.
//  1.0 MUSINSAGLOBAL 화주의 한글 헤더(MOMS_GRID_COLUMN_INFO.COLUMN_NM 결제일·출고예정일)는 2.0 헤더와 같아 따로 두지 않는다.
//  편집 컬럼만 읽고 감사 컬럼(생성·수정)은 버린다 — 서버가 찍는다.
// ExcelJS 는 업로드할 때만 불러온다(지연 로드). 여기서는 타입만 쓴다.
import type ExcelJS from 'exceljs'
import { PAIR_ORDER_SCHEDULE_EDIT_COLUMNS, PAIR_ORDER_SCHEDULE_MAX_SAVE_ROWS } from '@/services/master/pairOrderSchedule/consts'
import type { PairOrderScheduleEditKey } from '@/services/master/pairOrderSchedule/consts'

const headerToKey = new Map<string, PairOrderScheduleEditKey>([
  ...PAIR_ORDER_SCHEDULE_EDIT_COLUMNS.map(({ key, title }) => [title, key] as const),
  ...PAIR_ORDER_SCHEDULE_EDIT_COLUMNS.map(({ key }) => [key, key] as const),
])

const isErrorValue = (value: unknown) => typeof value === 'object' && value !== null && 'error' in value

/** 엑셀 날짜 셀은 ExcelJS 가 UTC 자정으로 준다 — 실행 환경 시간대와 무관하게 UTC 날짜를 쓴다. */
const ymd = (date: Date): string => date.toISOString().slice(0, 10).replace(/-/g, '')

/** 셀 값을 문자열로. 날짜 셀은 yyyyMMdd 로 맞춘다(이 화면의 날짜 컬럼 저장 형식). */
const cellText = (raw: ExcelJS.CellValue): string => {
  if (raw == null) return ''
  if (raw instanceof Date) return ymd(raw)
  if (typeof raw !== 'object') return String(raw)
  if (isErrorValue(raw)) return ''
  if ('richText' in raw) return raw.richText.map((run) => run.text).join('')
  if ('formula' in raw || 'sharedFormula' in raw) {
    const result = (raw as ExcelJS.CellFormulaValue).result
    if (isErrorValue(result)) return ''
    return result instanceof Date ? ymd(result) : String(result ?? '')
  }
  if ('text' in raw) return cellText((raw as { text: ExcelJS.CellValue }).text)
  return String(raw)
}

/** 첫 시트를 읽어 편집 컬럼만 담은 행 배열로 돌려준다. 헤더를 하나도 못 찾으면 던진다. */
export const parsePairOrderScheduleExcel = async (file: Blob): Promise<Record<PairOrderScheduleEditKey, string>[]> => {
  const { default: Excel } = await import('exceljs')
  const workbook = new Excel.Workbook()
  await workbook.xlsx.load(await file.arrayBuffer())
  const sheet = workbook.worksheets[0]
  if (!sheet) return []

  const headerKeys: (PairOrderScheduleEditKey | null)[] = []
  sheet.getRow(1).eachCell({ includeEmpty: true }, (cell, col) => {
    headerKeys[col] = headerToKey.get(cellText(cell.value).trim()) ?? null
  })
  if (!headerKeys.includes('CLS_DATE')) {
    throw new Error('1행에서 결제일 헤더를 찾지 못했습니다. 엑셀 다운로드 양식의 헤더를 그대로 써 주세요.')
  }

  const rows: Record<PairOrderScheduleEditKey, string>[] = []
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return
    const item: Record<PairOrderScheduleEditKey, string> = {
      CLS_DATE: '',
      CUT_SEQ: '',
      CUT_OFF_TIME: '',
      IS_CURRENT: '',
      FST_GI_END: '',
      LST_GI_END: '',
    }
    let hasValue = false
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      const key = headerKeys[col]
      if (!key) return
      const text = cellText(cell.value).trim()
      if (!text) return
      hasValue = true
      // 숫자 셀로 저장된 마감시각(9)은 두 자리(09)로 맞춘다 — 1.0 은 문자열 '09' 로 저장했다.
      item[key] = key === 'IS_CURRENT' ? text.toUpperCase() : key === 'CUT_OFF_TIME' && /^\d$/.test(text) ? `0${text}` : text
    })
    if (hasValue) rows.push(item)
  })
  // 업로드 행은 한 번의 [저장]으로 반영하므로 서버 저장 상한을 넘으면 미리 물린다.
  if (rows.length > PAIR_ORDER_SCHEDULE_MAX_SAVE_ROWS) {
    throw new Error(`한 번에 ${PAIR_ORDER_SCHEDULE_MAX_SAVE_ROWS}행까지 올릴 수 있습니다. (파일 ${rows.length}행) 나눠서 올려 주세요.`)
  }
  return rows
}
